// ./src/utils/api/PersonService.ts
//
// Person (contact / address book) service. OpResult-native (errors normalized in
// ApiService). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { listAllPages, ListAllOptions } from "./listAllPages";
import { Pagination, SortItem } from "./commonApiTypes";

// =============================================================================
// Constants (mirror the eventsmagic-api person boundary)
// =============================================================================

// The "upcoming birthday" window in days (mirrors PERSON_UPCOMING_BIRTHDAY_DAYS).
export const PERSON_UPCOMING_BIRTHDAY_DAYS = 30;

// =============================================================================
// GraphQL
// =============================================================================

const PERSON_FIELDS = `
  id
  accountId
  nameTitle
  firstName
  middleName
  lastName
  nameSuffix
  familyRelationship
  pronouns
  birthdayYear
  birthdayMonth
  birthdayDay
  birthday
  birthdayIncludesYear
  email
  phone
  personalWebsite
  socialUrl1
  socialUrl2
  socialUrl3
  country
  stateProvince
  city
  postalCode
  streetAddress
  extraAddress
  jobTitle
  companyDepartment
  company
  companyWebsite
  avatarUploadedFileId
  avatarUploadedFile { fileUrl thumbnailUrl }
  avatarGravatarUrl
  bio
  dietaryRestrictions
  optInForNotifications
  optInForMessages
  optInForReminders
  sex
  gender
  genderSelfDescribed
  sensitiveDataConsentAt
  resourceId
  notes
  personGroupId
  personGroupRole
  memberNumber
  status
  rights
  createdAt
  updatedAt
  tags { id name color }
`;

const PERSON_LIST_QUERY = gql`
  query PersonList($filter: PersonFilter, $params: PaginationAndSorting) {
    personList(filter: $filter, params: $params) {
      code
      data { ${PERSON_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PERSON_GET_QUERY = gql`
  query PersonGet($id: ID) {
    personGet(id: $id) {
      code
      data { ${PERSON_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PERSON_GET_MANY_QUERY = gql`
  query PersonGetMany($ids: [ID]) {
    personGetMany(ids: $ids) {
      code
      data { ${PERSON_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PERSON_CREATE_MUTATION = gql`
  mutation PersonCreate($params: PersonInput) {
    personCreate(params: $params) {
      code
      data { ${PERSON_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PERSON_UPDATE_MUTATION = gql`
  mutation PersonUpdate($where: PersonWhereInput, $params: PersonInput) {
    personUpdate(where: $where, params: $params) {
      code
      data { ${PERSON_FIELDS} }
      errors { name errors warnings }
    }
  }
`;

const PERSON_REMOVE_MUTATION = gql`
  mutation PersonRemove($where: PersonWhereInput) {
    personRemove(where: $where) {
      code
      data { id }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api Person schema)
// =============================================================================

export interface PersonTag {
  id?: string;
  name?: string | null;
  color?: string | null;
}

export interface Person {
  id?: string;
  accountId?: string;
  nameTitle?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  nameSuffix?: string | null;
  familyRelationship?: string | null;
  pronouns?: string | null;
  birthdayYear?: number | null;
  birthdayMonth?: number | null;
  birthdayDay?: number | null;
  /** Derived date (placeholder year 2000 when the year is unknown). */
  birthday?: string | null;
  birthdayIncludesYear?: boolean | null;
  email?: string | null;
  phone?: string | null;
  personalWebsite?: string | null;
  socialUrl1?: string | null;
  socialUrl2?: string | null;
  socialUrl3?: string | null;
  country?: string | null;
  stateProvince?: string | null;
  city?: string | null;
  postalCode?: string | null;
  streetAddress?: string | null;
  extraAddress?: string | null;
  jobTitle?: string | null;
  companyDepartment?: string | null;
  company?: string | null;
  companyWebsite?: string | null;
  avatarUploadedFileId?: string | null;
  /** Resolved avatar file (ms_storage) + server-computed Gravatar fallback — for `personAvatarUrl`. */
  avatarUploadedFile?: { fileUrl?: string | null; thumbnailUrl?: string | null } | null;
  avatarGravatarUrl?: string | null;
  bio?: string | null;
  dietaryRestrictions?: string | null;
  optInForNotifications?: boolean | null;
  optInForMessages?: boolean | null;
  optInForReminders?: boolean | null;
  sex?: string | null;
  gender?: string | null;
  genderSelfDescribed?: string | null;
  sensitiveDataConsentAt?: string | null;
  resourceId?: string | null;
  notes?: string | null;
  personGroupId?: string | null;
  personGroupRole?: string | null;
  memberNumber?: string | null;
  status?: number;
  /** Actions the current caller may perform ('edit' | 'remove' | 'duplicate'). */
  rights?: string[] | null;
  createdAt?: string;
  updatedAt?: string;
  tags?: PersonTag[] | null;
}

export interface PersonInput {
  /** The contact's photo (ms_storage file id) — #69. */
  avatarUploadedFileId?: string | null;
  nameTitle?: string | null;
  firstName?: string | null;
  middleName?: string | null;
  lastName?: string | null;
  nameSuffix?: string | null;
  familyRelationship?: string | null;
  pronouns?: string | null;
  birthdayYear?: number | null;
  birthdayMonth?: number | null;
  birthdayDay?: number | null;
  email?: string | null;
  phone?: string | null;
  personalWebsite?: string | null;
  socialUrl1?: string | null;
  socialUrl2?: string | null;
  socialUrl3?: string | null;
  country?: string | null;
  stateProvince?: string | null;
  city?: string | null;
  postalCode?: string | null;
  streetAddress?: string | null;
  extraAddress?: string | null;
  jobTitle?: string | null;
  companyDepartment?: string | null;
  company?: string | null;
  companyWebsite?: string | null;
  bio?: string | null;
  dietaryRestrictions?: string | null;
  optInForNotifications?: boolean | null;
  optInForMessages?: boolean | null;
  optInForReminders?: boolean | null;
  sex?: string | null;
  gender?: string | null;
  genderSelfDescribed?: string | null;
  sensitiveDataConsentAt?: string | null;
  resourceId?: string | null;
  notes?: string | null;
  personGroupId?: string | null;
  personGroupRole?: string | null;
  memberNumber?: string | null;
  status?: number | null;
  /** Replace the whole tag set ([] detaches all). */
  tagIds?: string[] | null;
  /** Merge these tags in (bulk assign). */
  addTagIds?: string[] | null;
  /** Detach these tags (bulk remove). */
  removeTagIds?: string[] | null;
}

// A bulk update (>1 id) may only mass-change these fields server-side (plus tag
// add/remove via addTagIds/removeTagIds).
export interface PersonBulkInput {
  personGroupId?: string | null;
  personGroupRole?: string | null;
  company?: string | null;
  companyWebsite?: string | null;
  country?: string | null;
  stateProvince?: string | null;
  city?: string | null;
  postalCode?: string | null;
  streetAddress?: string | null;
  extraAddress?: string | null;
  status?: number | null;
  addTagIds?: string[] | null;
  removeTagIds?: string[] | null;
}

export interface PersonFilter {
  id?: string[] | null;
  email?: string[] | null;
  searchKeyword?: string | null;
  /** Only contacts carrying ANY of these tags. */
  tagIds?: string[] | null;
  /** Only contacts belonging to these groups. */
  personGroupId?: string[] | null;
  /** Narrow to specific lifecycle statuses (omit = active + disabled). */
  status?: number[] | null;
  /** Only contacts whose next birthday is within the upcoming window. */
  upcomingBirthday?: boolean | null;
}

export interface PersonWhereInput {
  /** One id, or many for a bulk update/remove. */
  id?: string | string[];
}

// =============================================================================
// Derived helpers (display name, birthday) — shared by list + export
// =============================================================================

const MONTHS_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** A contact's display name from its parts, falling back to email / phone / company. */
export const getPersonDisplayName = (person: Person): string => {
  const name = [person.firstName, person.middleName, person.lastName]
    .map((p) => (p || "").trim())
    .filter(Boolean)
    .join(" ");

  return name || person.email || person.phone || person.company || "—";
};

/** A short birthday label ("Mar 4" or "Mar 4, 1990"), or null when no birthday. */
export const getPersonBirthdayLabel = (person: Person): string | null => {
  if (person.birthdayMonth == null || person.birthdayDay == null) {
    return null;
  }

  const mon = MONTHS_SHORT[person.birthdayMonth - 1] ?? String(person.birthdayMonth);
  const base = `${mon} ${person.birthdayDay}`;

  if (person.birthdayIncludesYear && person.birthdayYear != null) {
    return `${base}, ${person.birthdayYear}`;
  }

  return base;
};

/**
 * Whether the contact's next birthday (month/day, year-agnostic) falls within
 * `withinDays` days — mirrors the server's upcoming-birthday facet. Computed in a
 * fixed leap year (2000) so Feb 29 is valid and both sides share one day-of-year
 * frame; the year-end wrap is handled with mod 366.
 */
export const isPersonUpcomingBirthday = (
  person: Person,
  withinDays: number = PERSON_UPCOMING_BIRTHDAY_DAYS
): boolean => {
  const bm = person.birthdayMonth;
  const bd = person.birthdayDay;

  if (bm == null || bd == null) {
    return false;
  }

  const dayOfYear = (year: number, month: number, day: number): number => {
    const start = Date.UTC(year, 0, 0);
    const date = Date.UTC(year, month - 1, day);

    return Math.floor((date - start) / 86400000);
  };

  const safeDay = bm === 2 && bd > 28 ? 28 : bd;
  const now = new Date();
  const birthdayDoy = dayOfYear(2000, bm, safeDay);
  const todayDoy = dayOfYear(2000, now.getMonth() + 1, now.getDate());
  const diff = ((birthdayDoy - todayDoy + 366) % 366);

  return diff <= withinDays;
};

// =============================================================================
// Service
// =============================================================================

export class PersonService {
  static async list(
    filter?: PersonFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    return ApiService.executeQuery(PERSON_LIST_QUERY, {
      filter: filter || {},
      params: { pagination: pagination || undefined, sortBy: sortBy || undefined },
    });
  }

  /**
   * Fetches every contact matching `filter` by paging through the list. Used for
   * CSV export. Returns OpResult.ok(allRows), or the first failing page's result.
   * Paged by
   * `listAllPages`: above 50,000 rows it REFUSES rather than cuts, unless `maxRows` states a stop (ROADMAP #118).
   */
  static async listAll(
    filter?: PersonFilter,
    sortBy?: SortItem[],
    opts?: ListAllOptions
  ): Promise<OpResult> {
    return listAllPages<Person>(PersonService.list, filter, sortBy, opts);
  }

  static async get(id: string): Promise<OpResult> {
    return ApiService.executeQuery(PERSON_GET_QUERY, { id });
  }

  static async getMany(ids: string[]): Promise<OpResult> {
    return ApiService.executeQuery(PERSON_GET_MANY_QUERY, { ids });
  }

  static async create(params: PersonInput): Promise<OpResult> {
    return ApiService.executeMutation(PERSON_CREATE_MUTATION, { params });
  }

  static async update(where: PersonWhereInput, params: PersonInput): Promise<OpResult> {
    return ApiService.executeMutation(PERSON_UPDATE_MUTATION, { where, params });
  }

  static async remove(id: string): Promise<OpResult> {
    return ApiService.executeMutation(PERSON_REMOVE_MUTATION, { where: { id } });
  }

  /**
   * Mass-changes the bulk-writable fields (and/or adds/removes tags) on every
   * selected contact in one call (the server restricts a multi-id update to that
   * subset). Pass only the fields to change.
   */
  static async bulkUpdate(ids: string[], params: PersonBulkInput): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(PERSON_UPDATE_MUTATION, { where: { id: ids }, params });
  }

  /** Remove every selected contact in one call. */
  static async removeMany(ids: string[]): Promise<OpResult> {
    if (ids.length === 0) {
      return new OpResult();
    }

    return ApiService.executeMutation(PERSON_REMOVE_MUTATION, { where: { id: ids } });
  }
}
